import { createContext, useContext, useState, useEffect } from 'react'
import { authApi } from '../services/authApi'
import { setTenantId } from '../config/tenant'
import { jwtRequiresPasswordChange, jwtExpiresAtMs } from '../utils/jwt'
import { storageGet, storageSet, storageRemove } from '../utils/safeStorage'
import { setDateTimePreferences } from '../utils/format'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Aplica o formato de data/hora preferido do usuário globalmente (utils/format.js).
  useEffect(() => {
    setDateTimePreferences({
      dateFormat: user?.dateFormat ?? user?.DateFormat ?? null,
      timeFormat: user?.timeFormat ?? user?.TimeFormat ?? null,
    })
  }, [user])

  // Carregar dados do localStorage ao inicializar
  useEffect(() => {
    try {
      const storedToken = storageGet('token')
      const storedUser = storageGet('user')

      if (storedToken && storedUser) {
        try {
          const user = JSON.parse(storedUser)
          setToken(storedToken)
          setUser(user)
          setMustChangePassword(jwtRequiresPasswordChange(storedToken))

          if (user?.tenantId || user?.TenantId) {
            const tenantId = user.tenantId || user.TenantId
            setTenantId(tenantId)
          }

          // Refresca o usuário do servidor (pega campos novos como celular/formato sem relogin). Best-effort.
          authApi
            .getMe()
            .then((fresh) => {
              if (fresh) {
                setUser(fresh)
                storageSet('user', JSON.stringify(fresh))
              }
            })
            .catch(() => {})
        } catch (error) {
          console.error('Erro ao carregar dados de autenticação:', error)
          storageRemove('token')
          storageRemove('user')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Renovação proativa do JWT enquanto o SPA está aberto: confere periodicamente e, quando faltam
  // menos de 48h para expirar, chama /auth/refresh e estende a sessão (mantém logado sem relogin).
  // Best-effort: se o refresh falhar, mantém o token atual; o interceptor 401 trata a expiração real.
  useEffect(() => {
    if (!token) return
    const REFRESH_WHEN_LEFT_MS = 48 * 60 * 60 * 1000
    const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000
    let cancelled = false

    const maybeRefresh = async () => {
      const exp = jwtExpiresAtMs(token)
      if (exp == null || exp - Date.now() > REFRESH_WHEN_LEFT_MS) return
      try {
        const data = await authApi.refresh()
        const fresh = data?.token || data?.Token
        if (fresh && !cancelled) {
          setToken(fresh)
          storageSet('token', fresh)
          setMustChangePassword(jwtRequiresPasswordChange(fresh))
        }
      } catch {
        // mantém o token atual
      }
    }

    maybeRefresh()
    const id = setInterval(maybeRefresh, CHECK_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [token])

  const login = async (username, password, tenantId = null) => {
    try {
      const response = await authApi.login(username, password, tenantId)
      return consumeAuthResponse(response)
    } catch (error) {
      console.error('Erro ao fazer login:', error)
      return { 
        success: false, 
        error: error.message || 'Erro ao fazer login. Verifique suas credenciais.' 
      }
    }
  }

  const completeSso = async (provider, payload) => {
    try {
      const response = await authApi.completeSso(provider, payload)
      return consumeAuthResponse(response)
    } catch (error) {
      console.error('Erro ao concluir SSO:', error)
      return {
        success: false,
        error: error.message || 'Erro ao concluir login SSO.',
      }
    }
  }

  const completeLoginMfa = async (mfaTicket, code) => {
    try {
      const response = await authApi.loginWithMfa(mfaTicket, code)
      return consumeAuthResponse(response)
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Código do autenticador inválido.',
      }
    }
  }

  const register = async (name, email) => {
    try {
      const response = await authApi.register(name, email)
      return consumeAuthResponse(response)
    } catch (error) {
      console.error('Erro ao cadastrar usuário:', error)
      return {
        success: false,
        error: error.message || 'Erro ao cadastrar conta.',
      }
    }
  }

  const consumeAuthResponse = (response) => {
    const requiresTenantSelection =
      response.requiresTenantSelection === true || response.RequiresTenantSelection === true

    if (requiresTenantSelection) {
      return {
        success: false,
        requiresTenantSelection: true,
        tenants: response.tenants || response.Tenants || [],
        ssoPendingToken: response.ssoPendingToken || response.SsoPendingToken || null,
        isGlobalUser: response.isGlobalUser === true || response.IsGlobalUser === true,
      }
    }

    const isOrphanPendingApproval =
      response.isOrphanPendingApproval === true || response.IsOrphanPendingApproval === true
    if (isOrphanPendingApproval) {
      return {
        success: false,
        isOrphanPendingApproval: true,
        error:
          response.message ||
          response.Message ||
          'Conta cadastrada, mas sem associação com tenant. Solicite aprovação ao responsável.',
      }
    }

    const requiresMfa = response.requiresMfa === true || response.RequiresMfa === true
    if (requiresMfa) {
      return {
        success: false,
        requiresMfa: true,
        mfaTicket: response.mfaTicket || response.MfaTicket || null,
      }
    }

    if (!response?.token || !response?.user) {
      return {
        success: false,
        error: 'Resposta de login inválida',
      }
    }

    // Salvar token e usuário
    setToken(response.token)
    setUser(response.user)
    const needCh =
      response.mustChangePassword === true ||
      response.MustChangePassword === true ||
      jwtRequiresPasswordChange(response.token)
    setMustChangePassword(needCh)
    storageSet('token', response.token)
    storageSet('user', JSON.stringify(response.user))

    if (response.user?.tenantId || response.user?.TenantId) {
      const tenantId = response.user.tenantId || response.user.TenantId
      setTenantId(tenantId)
    }

    return { success: true, mustChangePassword: needCh, token: response.token, user: response.user }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    setMustChangePassword(false)
    storageRemove('token')
    storageRemove('user')
  }

  const completePasswordChange = (data) => {
    setToken(data.token)
    setUser(data.user)
    setMustChangePassword(false)
    storageSet('token', data.token)
    storageSet('user', JSON.stringify(data.user))
    if (data.user?.tenantId || data.user?.TenantId) {
      setTenantId(data.user.tenantId || data.user.TenantId)
    }
  }

  const updateUserProfilePhoto = (profilePhotoBase64) => {
    setUser((current) => {
      if (!current) return current
      const updated = { ...current, profilePhotoBase64: profilePhotoBase64 || null }
      storageSet('user', JSON.stringify(updated))
      return updated
    })
  }

  // Atualiza celular + opt-in de WhatsApp do usuário logado no contexto após salvar.
  const updateUserContact = ({ phoneNumber, whatsAppAlertsEnabled }) => {
    setUser((current) => {
      if (!current) return current
      const updated = {
        ...current,
        phoneNumber: phoneNumber ?? null,
        whatsAppAlertsEnabled: !!whatsAppAlertsEnabled,
      }
      storageSet('user', JSON.stringify(updated))
      return updated
    })
  }

  // Atualiza o formato de data/hora preferido (aplicado globalmente pelo effect acima).
  const updateUserPreferences = ({ dateFormat, timeFormat, timezone }) => {
    setUser((current) => {
      if (!current) return current
      const updated = { ...current, dateFormat: dateFormat ?? null, timeFormat: timeFormat ?? null, timezone: timezone ?? null }
      storageSet('user', JSON.stringify(updated))
      return updated
    })
  }

  const switchTenant = async (tenantId) => {
    try {
      const response = await authApi.switchTenant(tenantId)
      return consumeAuthResponse(response)
    } catch (error) {
      console.error('Erro ao trocar tenant:', error)
      return {
        success: false,
        error: error.message || 'Erro ao trocar de tenant.',
      }
    }
  }

  const isAuthenticated = !!token && !!user

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        mustChangePassword,
        login,
        completeLoginMfa,
        completeSso,
        register,
        logout,
        completePasswordChange,
        updateUserProfilePhoto,
        updateUserContact,
        updateUserPreferences,
        switchTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider')
  }
  return context
}
